import React, { useEffect, useState } from "react";
import Header from "../components/Header";
import StickyFooter from "../components/StickyFooter";
import { useTheme } from "@mui/material";
import {
  Box,
  Typography,
  Paper,
  TextField,
  Button,
  InputAdornment,
  MenuItem,
  Card,
  Dialog,
  Snackbar,
  CircularProgress,
} from "@mui/material";
import MuiAlert from "@mui/material/Alert";
import {
  AccountBalanceWallet,
  PersonOutline,
  Numbers,
  AccountBalance,
  CreditCard,
  InfoOutlined,
  ArrowBackIosNew,
} from "@mui/icons-material";
import LockIcon from "@mui/icons-material/Lock";
import SmsIcon from "@mui/icons-material/Sms";
import MarkEmailReadIcon from "@mui/icons-material/MarkEmailRead";
import { motion } from "framer-motion";
import api, { ensureCsrfToken } from "../utils/api";
import Cookies from "js-cookie";

const transferTypes = ["Online Banking", "Mobile Transfer", "USSD Transfer"];

const LocalTransfer = () => {
  const theme = useTheme();

  // Form states
  const [amount, setAmount] = useState("");
  const [beneficiaryName, setBeneficiaryName] = useState("");
  const [beneficiaryAccount, setBeneficiaryAccount] = useState("");
  const [bankName, setBankName] = useState("");
  const [transferType, setTransferType] = useState(transferTypes[0]);
  const [description, setDescription] = useState("");

  // Balance (fetched)
  const [availableBalance, setAvailableBalance] = useState(0);

  // Codes
  const [codes, setCodes] = useState({
    secureKey: "",
    otpCode: "",
    tacCode: "",
    digitalCvv: "",
  });
  const [codeStep, setCodeStep] = useState(0);
  const [verifying, setVerifying] = useState(false);
  const [shakeKey, setShakeKey] = useState(0);

  // Modal + status
  const [openVerify, setOpenVerify] = useState(false);
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  // Snackbar
  const [snack, setSnack] = useState({
    open: false,
    msg: "",
    severity: "error",
  });

  // Fetch balance
  useEffect(() => {
    (async () => {
      try {
        const res = await api.get("/api/user", { withCredentials: true });
        const bal = Number(res?.data?.current_balance ?? 0);
        setAvailableBalance(bal);
      } catch {}
    })();
  }, []);

  const presetAmounts = [100, 500, 1000];

  const glassExplain = {
    p: 1.5,
    borderRadius: 2,
    bgcolor: "rgba(14,165,233,0.06)",
    border: "1px dashed rgba(14,165,233,0.25)",
  };

  const codeMeta = [
    {
      key: "secureKey",
      title: "Secure Key",
      desc: "Used to authorize withdrawals.",
      Icon: LockIcon,
      maxLength: 6,
    },
    {
      key: "otpCode",
      title: "OTP Code",
      desc: "A one-time password sent to your mobile.",
      Icon: SmsIcon,
      maxLength: 6,
    },
    {
      key: "tacCode",
      title: "TAC Code",
      desc: "Transaction Authorization Code sent to your email.",
      Icon: MarkEmailReadIcon,
      maxLength: 6,
    },
    {
      key: "digitalCvv",
      title: "Digital CVV",
      desc: "3-digit digital CVV for this transfer.",
      Icon: CreditCard,
      maxLength: 3,
    },
  ];

  // Start verification flow
  const startVerification = () => {
    const amt = parseFloat(String(amount).replace(/,/g, "")) || 0;

    if (!beneficiaryName || !beneficiaryAccount || !bankName || !transferType) {
      return setSnack({
        open: true,
        msg: "Please complete beneficiary details.",
        severity: "error",
      });
    }
    if (!amt || amt <= 0) {
      return setSnack({
        open: true,
        msg: "Enter a valid transfer amount.",
        severity: "error",
      });
    }
    if (amt > availableBalance) {
      return setSnack({
        open: true,
        msg: "Insufficient funds for this transfer.",
        severity: "error",
      });
    }
    setStatus(null);
    setCodeStep(0);
    setOpenVerify(true);
  };

  // Verify one step
  const verifyCurrentStep = async () => {
    const meta = codeMeta[codeStep];
    if (!meta) return;

    const value = codes[meta.key].trim();
    if (!value || value.length < meta.maxLength) {
      setSnack({
        open: true,
        msg: `Please enter your ${meta.title}.`,
        severity: "error",
      });
      setShakeKey((k) => k + 1);
      return;
    }

    try {
      await ensureCsrfToken();
      const token = Cookies.get("XSRF-TOKEN");

      setVerifying(true);

      await api.post(
        "/api/verify-code",
        {
          type: meta.key,
          code: value,
          beneficiary_account: beneficiaryAccount,
          amount: parseFloat(String(amount).replace(/,/g, "")) || 0,
        },
        { headers: { "X-XSRF-TOKEN": token } }
      );

      setSnack({
        open: true,
        msg: `${meta.title} verified successfully.`,
        severity: "success",
      });

      if (codeStep < codeMeta.length - 1) {
        setCodeStep((s) => s + 1);
      } else {
        await handleTransfer();
      }
    } catch (err) {
      const msg =
        err?.response?.data?.error ||
        `Verification failed for ${meta.title}. Try again.`;
      setSnack({ open: true, msg, severity: "error" });
      setShakeKey((k) => k + 1);
    } finally {
      setVerifying(false);
    }
  };

  // Final transfer
  const handleTransfer = async () => {
    try {
      setLoading(true);
      await ensureCsrfToken();
      const token = Cookies.get("XSRF-TOKEN");

      await api.post(
        "/api/local-transfer",
        {
          beneficiary_name: beneficiaryName,
          beneficiary_account: beneficiaryAccount,
          bank_name: bankName,
          transfer_type: transferType,
          amount,
          description,
          secure_key: codes.secureKey,
          otp_code: codes.otpCode,
          tac_code: codes.tacCode,
          digital_cvv: codes.digitalCvv,
        },
        { headers: { "X-XSRF-TOKEN": token } }
      );

      setStatus("success");
      setSnack({
        open: true,
        msg: "Transfer successful.",
        severity: "success",
      });
    } catch (err) {
      const msg = err?.response?.data?.error || "Transfer failed. Please try again.";
      setErrorMessage(msg);
      setStatus("failed");
      setSnack({ open: true, msg, severity: "error" });
    } finally {
      setLoading(false);
    }
  };

  const renderStep = () => {
    const meta = codeMeta[codeStep];
    if (!meta) return null;
    const Icon = meta.Icon;

    return (
      <motion.div
        key={`${meta.key}-${shakeKey}`}
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.35 }}
      >
        <Typography variant="h6" fontWeight={700} sx={{ display: "flex", alignItems: "center", mb: 1 }}>
          <Icon sx={{ mr: 1, color: "#0ea5e9" }} />
          {meta.title}
        </Typography>

        <Paper elevation={0} sx={glassExplain}>
          <Typography variant="caption" color="text.secondary">
            {meta.desc}
          </Typography>
        </Paper>

        <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.25 }} style={{ marginTop: 12 }}>
          <TextField
            fullWidth
            label={`Enter ${meta.title}`}
            type="password"
            value={codes[meta.key]}
            onChange={(e) => setCodes((c) => ({ ...c, [meta.key]: e.target.value }))}
            inputProps={{ maxLength: meta.maxLength, style: { letterSpacing: "6px" } }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Icon sx={{ color: "#0ea5e9" }} />
                </InputAdornment>
              ),
              endAdornment: verifying && (
                <InputAdornment position="end">
                  <CircularProgress size={20} sx={{ color: "#0ea5e9" }} />
                </InputAdornment>
              ),
            }}
          />
        </motion.div>

        <Box display="flex" justifyContent="flex-end" gap={1.5} mt={2}>
          {codeStep > 0 && (
            <Button onClick={() => setCodeStep((s) => s - 1)} disabled={verifying}>
              Back
            </Button>
          )}
          <Button
            onClick={verifyCurrentStep}
            disabled={verifying}
            variant="contained"
            sx={{
              borderRadius: 2,
              px: 3,
              py: 1,
              fontWeight: 600,
              background: "linear-gradient(90deg, #0ea5e9, #2F80ED)",
            }}
          >
            {verifying ? "Verifying..." : "Verify & Continue"}
          </Button>
        </Box>
      </motion.div>
    );
  };

  return (
    <Box
      p={{ xs: 2, md: 4 }}
      sx={{
        minHeight: "100vh",
        width: "100%",
        backgroundColor: theme.palette.background.default,
        color: theme.palette.text.primary,
        position: "absolute",
        left: 0,
        right: 0,
        px: 3,
        py: 4,
      }}
    >
      <Header />

      {/* Breadcrumb */}
      <Typography variant="body2" fontSize="12px" marginTop="20%" color="text.secondary" mb={1}>
        Dashboard &gt; Local Transfer
      </Typography>

      {/* Page Title */}
      <Typography variant="h6" fontWeight={600} mb={2}>
        Local Transfer
      </Typography>

      {/* Header Card */}
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }}>
        <Paper
          elevation={3}
          sx={{
            borderRadius: 3,
            background: "linear-gradient(to bottom, #3AB7F9, #0164C1)",
            color: "#fff",
            px: 2,
            pt: 3,
            pb: 4,
            textAlign: "center",
            mb: 3,
          }}
        >
          <CreditCard sx={{ fontSize: 40, mb: 1 }} />
          <Typography variant="h6" fontWeight={600}>
            Local Bank Transfer
          </Typography>
          <Typography fontSize="0.85rem" sx={{ opacity: 0.9 }}>
            Send money to any local bank account securely
          </Typography>
        </Paper>
      </motion.div>

      {/* Form Card */}
      <Card
        elevation={2}
        sx={{
          p: { xs: 2, sm: 3 },
          borderRadius: 4,
          maxWidth: 600,
          mx: "auto",
          mt: -2,
          mb: 8,
        }}
      >
        {/* Balance Widget */}
        <Paper
          variant="outlined"
          sx={{
            borderRadius: 3,
            p: 2,
            mb: 3,
            display: "flex",
            alignItems: "center",
            gap: 2,
          }}
        >
          <AccountBalanceWallet sx={{ color: "#0ea5e9" }} />
          <Box>
            <Typography fontSize="0.85rem" color="text.secondary">
              Available Balance
            </Typography>
            <Typography fontWeight={600}>${(availableBalance || 0).toLocaleString()}</Typography>
          </Box>
        </Paper>

        {/* Transfer Amount */}
        <Paper variant="outlined" sx={{ borderRadius: 3, p: 2, mb: 3 }}>
          <Typography variant="body2" fontWeight={600} mb={1}>
            Transfer Amount
          </Typography>
          <TextField
            fullWidth
            variant="outlined"
            placeholder="0.00"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            InputProps={{
              startAdornment: <InputAdornment position="start">$</InputAdornment>,
              endAdornment: <InputAdornment position="end">.00</InputAdornment>,
            }}
          />
          <Box display="flex" gap={1.5} mt={2} flexWrap="wrap">
            {presetAmounts.map((amt) => (
              <Button
                key={amt}
                variant="contained"
                onClick={() => setAmount(String(amt))}
                sx={{
                  background: "#f1f5f9",
                  color: "#0f172a",
                  minWidth: "80px",
                  fontWeight: 500,
                  "&:hover": { background: "#e2e8f0" },
                }}
              >
                ${amt}
              </Button>
            ))}
          </Box>
        </Paper>

        {/* Beneficiary Section */}
        <Typography variant="subtitle2" fontWeight={600} sx={{ display: "flex", alignItems: "center", mb: 1 }}>
          <PersonOutline sx={{ color: "#0ea5e9", mr: 1 }} /> Beneficiary Details
        </Typography>
        <Paper variant="outlined" sx={{ borderRadius: 3, p: 2, mb: 3 }}>
          <TextField
            label="Beneficiary Account Name"
            fullWidth
            margin="normal"
            value={beneficiaryName}
            onChange={(e) => setBeneficiaryName(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <PersonOutline />
                </InputAdornment>
              ),
            }}
          />
          <TextField
            label="Beneficiary Account Number"
            fullWidth
            margin="normal"
            value={beneficiaryAccount}
            onChange={(e) => setBeneficiaryAccount(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Numbers />
                </InputAdornment>
              ),
            }}
          />
          <TextField
            label="Bank Name"
            fullWidth
            margin="normal"
            value={bankName}
            onChange={(e) => setBankName(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <AccountBalance />
                </InputAdornment>
              ),
            }}
          />
          <TextField
            label="Transfer Type"
            select
            fullWidth
            value={transferType}
            onChange={(e) => setTransferType(e.target.value)}
            margin="normal"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <CreditCard />
                </InputAdornment>
              ),
            }}
          >
            {transferTypes.map((type) => (
              <MenuItem key={type} value={type}>
                {type}
              </MenuItem>
            ))}
          </TextField>
        </Paper>

        {/* Additional Info */}
        <Typography variant="subtitle2" fontWeight={600} sx={{ display: "flex", alignItems: "center", mb: 1 }}>
          <InfoOutlined sx={{ color: "#0ea5e9", mr: 1 }} /> Additional Information
        </Typography>
        <Paper variant="outlined" sx={{ borderRadius: 3, p: 2, mb: 3 }}>
          <TextField
            label="Description/Memo"
            fullWidth
            multiline
            minRows={3}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <InfoOutlined />
                </InputAdornment>
              ),
            }}
          />
        </Paper>

        {/* Buttons */}
        <Box display="flex" flexDirection="column" gap={2} mt={2}>
          <Button
            fullWidth
            variant="contained"
            sx={{
              py: 1.2,
              background: "linear-gradient(135deg, #0ea5e9 10%, #2F80ED 90%)",
              fontWeight: 600,
              color: "#fff",
              "&:hover": {
                transform: "scale(1.02)",
                boxShadow: "0 6px 18px rgba(14,165,233,0.35)",
              },
            }}
            onClick={startVerification}
          >
            Start Verification
          </Button>
          <Button fullWidth variant="outlined" href="/home" startIcon={<ArrowBackIosNew />} sx={{ py: 1.2, fontWeight: 600 }}>
            Back to Dashboard
          </Button>
        </Box>
      </Card>

       {/* Verification Modal */}
      <Dialog
        open={openVerify}
        onClose={() => (status ? setOpenVerify(false) : null)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: 4, p: 3 } }}
      >
        {status === null && renderStep()}
        {status === "success" && (
          <Typography align="center" color="green" fontWeight={600}>Transfer Successful ✔</Typography>
        )}
        {status === "failed" && (
          <Typography align="center" color="error" fontWeight={600}>{errorMessage}</Typography>
        )}
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snack.open}
        autoHideDuration={3000}
        onClose={() => setSnack((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <MuiAlert severity={snack.severity} elevation={3} variant="filled" sx={{ width: "100%" }}>
          {snack.msg}
        </MuiAlert>
      </Snackbar>

      <StickyFooter />
    </Box>
  );
};

export default LocalTransfer;